import FormControl from "@mui/material/FormControl";
import { Chip } from '@mui/material';
import TextField from '@mui/material/TextField';
import React, { useState } from 'react';
import { WrappedFieldProps } from 'redux-form/lib/Field';
import { FieldValidator } from '../validation/validators/fieldValidator';

const ENTER_KEY = 'Enter';
const INITIAL_TEXT_FIELD_VALUE = '';
const DEFAULT_HELPER_TEXT = 'Type value and press enter key to add'
const UNIQUE_ERROR_MESSAGE = 'Value must be unique';
const NO_ERROR = undefined;

type Props = WrappedFieldProps & {
  readonly label: string;
  readonly placeholder?: string;
  readonly hintText?: string;
  readonly fullWidth?: boolean;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly unique?: boolean;
  readonly itemValidator?: FieldValidator<string>;
};

const MultiTextFormField = ({
  label,
  placeholder,
  hintText = DEFAULT_HELPER_TEXT,
  fullWidth = true,
  required = false,
  disabled = false,
  unique = true,
  itemValidator,
  meta: { touched, error },
  input: { value, onChange, onBlur },
}: Props) => {
  const [items, setItems] = useState<string[]>(value || []);
  const [textFieldValue, setTextFieldValue] = useState<string>(INITIAL_TEXT_FIELD_VALUE);

  const [itemError, setItemError] = useState<string | undefined>(NO_ERROR);

  const hasItemError = itemError !== NO_ERROR;
  const hasFieldError = touched && Boolean(error);

  const hasError = hasItemError || hasFieldError;
  const errorMessage = itemError !== NO_ERROR ? itemError : error;

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setItemError(NO_ERROR);
    setTextFieldValue(event.target.value);
  };

  const handleAddItem = (item: string) => {
    if (!item) {  // ignore empty string
      return;
    }

    if (itemValidator && !itemValidator.isValid(item)) {
      setItemError(itemValidator.errorMessage);
    } else if (unique && items.includes(item)) {
      setItemError(UNIQUE_ERROR_MESSAGE);
    } else {
      const newItems = [...items, item];

      setItems(newItems);

      setItemError(NO_ERROR);
      setTextFieldValue(INITIAL_TEXT_FIELD_VALUE);
    }
  };

  const handleDeleteItem = (item: string, index: number) => {
    const newItems = items.filter((it, idx) => idx !== index);

    setItems(newItems);
    onChange(newItems);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ENTER_KEY) {
      event.preventDefault();

      handleAddItem((event.target as HTMLInputElement).value);
      onChange(items);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    handleAddItem(event.target.value);

    onBlur(items);
  };

  return (
    <FormControl fullWidth={fullWidth}>
      <TextField
        label={label}
        placeholder={placeholder}
        variant='standard'
        value={textFieldValue}
        onChange={handleChangeValue}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
        error={hasError}
        helperText={hasError ? errorMessage : hintText}
      />
      <div>
        { items.map((item, index) => (
          <Chip
            key={index}
            size='small'
            sx={{ m: 0.5 }}
            label={item}
            disabled={disabled}
            onDelete={() => handleDeleteItem(item, index)}
          />
        ))}
      </div>
    </FormControl>
  );
};

export default MultiTextFormField;
